import React, { type PropsWithChildren } from "react";
import { Flex, SimpleGrid } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import { Button } from "ui/button";
import { Diptych, type DiptychProps } from "ui/diptych";
import { TextTitle } from "ui/text";
import {
  ExperimentsIcon,
  LightningIcon,
  MegaphoneIcon,
  PlusIcon,
  RefreshIcon,
  SettingsIcon,
  TargetIcon,
  TrendIcon,
  WarehouseIcon,
} from "ui/icons";
import { Rule } from "ui/rule";
import { integrationsCount } from "../../../constants/integrations";
import { UseCaseCard } from "./use-case-card";

function CardGroup({ children }: PropsWithChildren) {
  return (
    <SimpleGrid columns={3} gap={{ base: "tiny", sm: "smaller", md: "tiny", xl: "smallest" }} w="100%" mx="tiniest">
      {children}
    </SimpleGrid>
  );
}

const diptychProps: Pick<DiptychProps, "titleAs" | "size" | "theme" | "gap" | "mediaRatio"> = {
  size: "sm",
  gap: { base: "bigger", sm: "biggest", md: "bigger", lg: "huge" },
  mediaRatio: "none",
};

export function UseCasesByTeam() {
  return (
    <Section my={0} pb={sectionSpacingY}>
      <Rule />

      <TextTitle
        align="center"
        size="lg"
        title="Marketing and data teams thrive together here"
        maxW="700px"
        mt={{ base: "biggest", md: "huge" }}
      />
      <Flex flexDir="column" mt={{ base: "biggest", md: "huge" }} gap={sectionSpacingY}>
        <Diptych
          {...diptychProps}
          direction="right"
          title="Lifecycle and CRM teams"
          description="Use all of your organization’s data to power real-time personalization and optimize campaigns with a complete and simple set of features."
          actions={
            <Button hasRightChevron href="/solutions/marketing">
              Hightouch for Marketing
            </Button>
          }
          media={
            <CardGroup>
              <UseCaseCard
                icon={{ bgColor: "new.blue.300", color: "new.blue.900", icon: MegaphoneIcon }}
                title={`Personalize \ncampaigns`}
                href="/playbooks/product-utilization-emails-iterable"
              />
              <UseCaseCard
                icon={{ bgColor: "new.green.300", color: "new.green.50", icon: ExperimentsIcon }}
                title={`Experiment across \nchannels`}
                href="/platform/customer-studio"
              />
              <UseCaseCard
                icon={{ bgColor: "new.violet.700", color: "new.violet.150", icon: TrendIcon }}
                title={`Understand campaign \nperformance`}
                href="/platform/intelligence"
              />
            </CardGroup>
          }
        />

        <Diptych
          {...diptychProps}
          direction="left"
          title="Advertising teams"
          description="Combine the power of data onboarding and CDP activation in one tool to maximize revenue & ROAS."
          actions={
            <Button hasRightChevron href="/solutions/advertising">
              Hightouch for Advertising
            </Button>
          }
          media={
            <CardGroup>
              <UseCaseCard
                icon={{ bgColor: "new.yellow.300", color: "new.yellow.700", icon: LightningIcon }}
                title="Power conversion events"
                href="/playbooks/send-better-signals-with-facebook-conversion-api"
              />
              <UseCaseCard
                icon={{ bgColor: "new.blue.900", color: "new.blue.150", icon: TargetIcon }}
                title="Target & suppress audiences"
                href="/platform/customer-studio"
              />
              <UseCaseCard
                icon={{ bgColor: "new.red.500", color: "new.red.150", icon: PlusIcon }}
                title={`Increase\n match rates`}
                href="/platform/match-booster"
              />
            </CardGroup>
          }
        />

        <Diptych
          {...diptychProps}
          direction="right"
          title="Data teams"
          description="Use all of your organization’s data and data science to power personalization and campaigns, but without losing control."
          actions={
            <Button hasRightChevron href="/solutions/data">
              Hightouch for Data Teams
            </Button>
          }
          media={
            <CardGroup>
              <UseCaseCard
                icon={{ bgColor: "new.blue.500", color: "new.blue.50", icon: RefreshIcon }}
                title={`Sync data with \nReverse ETL`}
                href="/platform/reverse-etl"
              />
              <UseCaseCard
                icon={{ bgColor: "new.violet.700", color: "new.violet.150", icon: WarehouseIcon }}
                title={`Turn your warehouse \ninto a CDP`}
                href="/blog/friends-dont-let-friends-buy-a-cdp"
              />
              <UseCaseCard
                icon={{ bgColor: "new.green.700", color: "new.green.50", icon: SettingsIcon }}
                title={`Integrate with \n${integrationsCount}+ tools`}
                href="/integrations"
              />
            </CardGroup>
          }
        />
      </Flex>
    </Section>
  );
}
